import angular from 'angular';
import moment from 'moment';
import DocumentService from '@/shared/services/DocumentService';
import { isWord, isPdf, isExcel } from '@/shared/utils/mime';
import SignalRService from '@/shared/services/SignalRService';
import dataCacheService from '@/shared/services/DataCacheService';
import AuthService from '@/shared/services/AuthService';

//What document template types should be shown in the relation letters?
const templateTypes = [
  3, // Assignment letters
  10, // Authorization letters
  13, // EID
  18, // Questionnaire
  21, // ContractRentalAgreement
  22, // Inventory
  23, // ProvisionNote
  26, // CompositionStatement
  27, // TerminationConfirmation
  30, // Quotation
  99, // Others
];

const documentTypes = [
  11, //Assignment letters
  12, //Authorization letters
  29, //Generated document
  78, //QuestionnairePersonalIncomeTax
  93, //Inventory
  94, //ProvisionNote
  144, //TerminationConfirmation
  150, //CompositionStatement
  81, //Quotation
  5000, //ContractRentalAgreement
];

const ensureDate = value => {
  if (!value) return null;
  if (typeof value === 'string') return new moment(value.substr(0, 10)).toDate();
  if (moment.isMoment(value)) return value.toDate();
  if (value instanceof Date) return value;
  return value;
};

const mapDocument = ({
  createDate,
  documentDate,
  documentStartDate,
  documentEndDate,
  mime,
  relations,
  links,
  tags,
  ...other
}) => ({
  ...other,
  mime,
  createDate: ensureDate(createDate),
  documentDate: ensureDate(documentDate),
  documentStartDate: ensureDate(documentStartDate),
  documentEndDate: ensureDate(documentEndDate),
  isPdf: isPdf(mime),
  isWord: isWord(mime),
  isExcel: isExcel(mime),
});

const consolidateDocuments = documents => {
  return documents
    .filter(x => {
      if (!x.scannedDocumentId) return true;
      if (x.id === x.scannedDocumentId) return true;
      return !documents.some(y => y.id === x.scannedDocumentId);
    })
    .map(mapDocument);
};

angular.module('cxRelations').directive('cxRelGeneratedDocuments', function() {
  return {
    restrict: 'E',
    template: require('./relGeneratedDocuments.html'),
    scope: {
      relation: '=',
    },
    bindToController: true,
    controllerAs: 'vm',
    controller: [
      '$scope',
      '$http',
      'appUrls',
      'flySvc',
      'FileSaver',
      'dialogsSvc',
      'ModalService',
      '$timeout',
      'dataCache',
      '$translate',
      'appMessages',
      function(
        $scope,
        $http,
        appUrls,
        flySvc,
        FileSaver,
        dialogsSvc,
        ModalService,
        $timeout,
        dataCache,
        $translate,
        appMessages,
      ) {
        var vm = this;

        vm.$onInit = function() {
          vm.documents = [];
          vm.loading = true;
          vm.showInactive = false;
          const settings = dataCache.getCompanySettings(vm.relation.companyId);
          vm.hasSigningProvider = settings && settings.documentSigningProvider !== 0;

          vm.datepickerOptions = {
            startingDay: 1,
          };

          SignalRService.subscribe('documentSigningStatusUpdated', vm.documentUpdatedHandler);
          SignalRService.subscribe('documentUpdated', vm.documentUpdatedHandler);
          SignalRService.subscribe('documentDeleted', vm.documentDeletedHandler);
          SignalRService.subscribe('documentTagAccessUpdated', vm.documentTagAccessUpdatedHandler);
          $scope.$on('$destroy', function() {
            SignalRService.unsubscribe('documentSigningStatusUpdated', vm.documentUpdatedHandler);
            SignalRService.unsubscribe('documentUpdated', vm.documentUpdatedHandler);
            SignalRService.unsubscribe('documentDeleted', vm.documentDeletedHandler);
            SignalRService.unsubscribe(
              'documentTagAccessUpdated',
              vm.documentTagAccessUpdatedHandler,
            );
          });

          $http
            .get(`${appUrls.documents}/api/Documents/Links/${vm.relation.id}/3`, {
              params: { documentTypes },
            })
            .then(function(result) {
              vm.documents = consolidateDocuments(result.data);
              vm.loading = false;
            });
        };

        vm.isInactive = function(document) {
          return (
            !!document.documentEndDate &&
            moment.utc(document.documentEndDate).isBefore(moment.utc(), 'day')
          );
        };

        vm.hasInactive = function() {
          return (vm.documents || []).some(vm.isInactive);
        };

        vm.filterInactive = function(document) {
          if (vm.showInactive) return true;
          return !vm.isInactive(document);
        };

        vm.onPreviewClosed = () => {
          $timeout(function() {
            vm.showPreview = false;
            vm.activeDocumentId = null;
          }, 0);
        };

        vm.onPreviewSaved = document => {
          $timeout(function() {
            vm.showPreview = false;
            vm.activeDocument = null;
            vm.documents = vm.documents.map(x => {
              if (x.id === document.id) {
                return {
                  ...x,
                  ...mapDocument(document),
                };
              }
              return x;
            });
          }, 0);
        };

        vm.previewDocument = document => {
          vm.activeDocument = document;
          vm.showPreview = true;
        };

        vm.downloadDocument = function(document, asPdf) {
          const { id: documentId, name, mime } = document;
          if (!documentId) return;

          if (!asPdf || isPdf(mime)) {
            //Normal download
            document.showSpinner = true;
            DocumentService.download(documentId).finally(() => {
              $timeout(function() {
                document.showSpinner = false;
              }, 0);
            });
          } else {
            document.showSpinner = true;
            //Convert to pdf
            DocumentService.convertToPdf(documentId, name).finally(() => {
              $timeout(function() {
                document.showSpinner = false;
              }, 0);
            });
          }
        };

        vm.editDocument = function(document) {
          const language = $translate.use().split('-')[0];
          DocumentService.edit(document.id, language);
        };

        vm.deleteDocument = function(document) {
          dialogsSvc.showYesNo('comfirmDeleteTitle', 'msgConfirmDeleteDocument').then(function() {
            document.showSpinner = true;
            $http.delete(`${appUrls.documents}/api/Documents/${document.id}`).then(
              function() {
                flySvc.success();
                var index = vm.documents.indexOf(document);
                if (index >= 0) vm.documents.splice(index, 1);
              },
              function(error) {
                flySvc.error(error);
                document.showSpinner = false;
                return '';
              },
            );
          });
        };
        vm.deleteSelectedDocuments = function() {
          const selectedDocuments = vm.documents.filter(x => x.selected);
          if (!selectedDocuments || selectedDocuments.length === 0) return;

          dialogsSvc.showYesNo('comfirmDeleteTitle', 'msgConfirmDeleteDocuments').then(function() {
            selectedDocuments.map(document => {
              document.showSpinner = true;
              $http.delete(`${appUrls.documents}/api/Documents/${document.id}`).then(
                function() {
                  var index = vm.documents.indexOf(document);
                  if (index >= 0) vm.documents.splice(index, 1);
                },
                function(error) {
                  flySvc.error(error);
                  document.showSpinner = false;
                  return '';
                },
              );
            });
          });
        };
        vm.createDocument = function() {
          ModalService.showModal({
            template: require('../dialogs/generateRelationDocumentDialog.html'),
            controller: 'GenerateRelationDocumentDialogCtrl',
            controllerAs: 'vm',
            locationChangeSuccess: false,
            inputs: {
              relationId: vm.relation.id,
              companyId: vm.relation.companyId,
              language: vm.relation.languageCode,
              relationType: vm.relation.type, // relation type
              templateTypes,
            },
          }).then(function(modal) {
            modal.element.modal();
            modal.element.on('hidden.bs.modal', function(e) {
              modal.element.off(e); //Remove event
              modal.element.remove(); //Remove element
            });

            modal.close.then(function(result) {
              if (result && result.document) {
                if (result.openEmailDialog) {
                  vm.openEmailDocumentDialog(result.document, true);
                } else {
                  flySvc.success();
                  vm.documents.unshift(mapDocument(result.document));
                }
              }
            });
          });
        };

        vm.replaceDocument = function(files, document) {
          if (!files || files.length < 1) return;

          var data = new FormData();
          data.append('data', files[0].data);
          document.showSpinner = true;
          $http({
            method: 'post',
            url: appUrls.documents + '/api/Documents/Replace',
            params: { documentId: document.id },
            headers: {
              'Content-Type': undefined,
            },
            data: data,
          })
            .then(function(response) {
              var result = response.data;
              if (!result) return;
              angular.extend(document, mapDocument(result));
              flySvc.success();
            })
            .catch(function(error) {
              flySvc.error(error);
            })
            .finally(function() {
              document.showSpinner = false;
            });
        };

        vm.uploadDocument = function() {
          ModalService.showModal({
            template: require('../dialogs/uploadRelationDocumentDialog.html'),
            controller: 'UploadRelationDocumentDialogCtrl',
            controllerAs: 'vm',
            locationChangeSuccess: false,
            inputs: {
              relationId: vm.relation.id,
              documentTypes,
            },
          }).then(function(modal) {
            modal.element.modal();
            modal.element.on('hidden.bs.modal', function(e) {
              modal.element.off(e); //Remove event
              modal.element.remove(); //Remove element
            });

            modal.close.then(function(result) {
              if (result) {
                flySvc.success();
                if (result.length === 1) {
                  // If only one document returns, it couldn't be matched to another document
                  // Just add it to the list
                  vm.documents.push(mapDocument(result[0]));
                  return;
                } else if (result.length === 2) {
                  let original = null;
                  let scanned = null;

                  if (result[0].scannedDocumentId === result[1].id) {
                    [original, scanned] = result;
                  } else if (result[1].scannedDocumentId === result[0].id) {
                    [scanned, original] = result;
                  }
                  if (original != null && scanned != null) {
                    //The first document is the original
                    if (original != null) {
                      const idx = vm.documents.findIndex(x => x.id === original.id);
                      if (idx >= 0) {
                        vm.documents.splice(idx, 1); //Remove original from list
                      }
                    }
                    //Add scanned document
                    vm.documents.push(mapDocument(scanned));
                    return;
                  }
                }

                //Fallback merge docuemnts
                result.foreach(doc => {
                  var document = vm.documents.find(l => l.id == doc.id);
                  if (document != null) {
                    angular.extend(document, mapDocument(doc));
                  } else {
                    vm.documents.push(mapDocument(doc));
                  }
                });
              }
            });
          });
        };

        vm.markSigned = function(document) {
          if (document.signingStatus >= 10) return; //Already signed or pending
          document.showSpinner = true;
          const model = {
            documentId: document.id,
            signingStatus: 20, // SigningComplete
            signingProvider: 0, // None
          };
          return $http.put(appUrls.documents + '/api/Documents/Sign', model).then(
            function(result) {
              if (result && result.data) {
                angular.extend(document, mapDocument(result.data));
                flySvc.success();
              }
              document.showSpinner = false;
              return false;
            },
            function(error) {
              flySvc.error(error);
              document.showSpinner = false;
              return '';
            },
          );
        };

        vm.update = function(document, field, value) {
          if (field === 'documentDate' && !value) return ''; //Document date is required
          if (
            (field === 'documentStartDate' || field === 'documentEndDate') &&
            typeof value === 'undefined'
          )
            return '';

          var model = angular.copy(document);

          if (angular.equals(model[field], value)) return true;

          model[field] = value;

          if (model.documentDate instanceof Date) model.documentDate = model.documentDate.ISO();
          if (model.documentStartDate instanceof Date)
            model.documentStartDate = model.documentStartDate.ISO();
          if (model.documentEndDate instanceof Date)
            model.documentEndDate = model.documentEndDate.ISO();

          return $http.put(appUrls.documents + '/api/Documents/Update', model).then(
            function(result) {
              if (result && result.data) {
                angular.extend(document, mapDocument(result.data));
                flySvc.success();
              }
              return false;
            },
            function(error) {
              flySvc.error(error);
              return '';
            },
          );
        };

        vm.openEmailDocumentDialog = function(document, asPdf) {
          ModalService.showModal({
            template: require('../dialogs/mailDocumentDialog.html'),
            controller: 'MailDocumentDialogCtrl',
            controllerAs: 'vm',
            locationChangeSuccess: false,
            inputs: {
              document: document,
              asPdf: asPdf,
              relationId: vm.relation.id,
              companyId: vm.relation.companyId,
            },
          }).then(function(modal) {
            modal.element.modal();
            modal.element.on('hidden.bs.modal', function(e) {
              modal.element.off(e); //Remove event
              modal.element.remove(); //Remove element
            });
            modal.close.then(function(result) {
              if (result) {
                angular.extend(document, result);
              }
            });
          });
        };

        vm.openInApp = function(document) {
          DocumentService.openInApp(document.id);
        };

        vm.revokeSignatureRequest = function(document) {
          if (!vm.hasSigningProvider) {
            flySvc.error('MsgNoSigningProviderConfiguredSeeSettings');
            return;
          }

          dialogsSvc.showYesNo('comfirmTitle', 'msgConfirmRevokeSignature').then(function() {
            document.showSpinner = true;

            let url =
              appUrls.documentSigning +
              `/api/RevokeDocumentSigningRequest/${vm.relation.companyId}/${document.signingId}/`;
            if (document.signingPackageId) {
              url += `${document.signingPackageId}`;
            }
            $http.post(url).then(
              function(result) {
                if (result && result.data) {
                  document.showSpinner = false;
                  flySvc.success('msgDocumentSingingRequestRevoked');
                }
              },
              function(error) {
                document.showSpinner = false;
                flySvc.error(error);
              },
            );
          });
        };

        vm.openSignatureDialog = function(documents) {
          // use direct reference on datacache to prevent not being able to sign a document when provider was just configured
          var hasSigningProvider =
            dataCache.getCompanySettings(vm.relation.companyId).documentSigningProvider !== 0;

          if (!hasSigningProvider) {
            flySvc.error('MsgNoSigningProviderConfiguredSeeSettings');
            return;
          }

          ModalService.showModal({
            template: require('../dialogs/requestDocumentSignatureDialog.html'),
            controller: 'RequestDocumentSignatureDialogCtrl',
            controllerAs: 'vm',
            locationChangeSuccess: false,
            inputs: {
              documents: documents,
              companyId: vm.relation.companyId,
              relationId: vm.relation.id,
              languageCode: vm.relation.languageCode,
            },
          }).then(function(modal) {
            modal.element.modal();
            modal.element.on('hidden.bs.modal', function(e) {
              modal.element.off(e); //Remove event
              modal.element.remove(); //Remove element
            });
            modal.close.then(function(result) {
              if (!result) return;

              // signing request sent successfully, update document status.
              documents.map(x => {
                angular.extend(x, {
                  ...x,
                  signingStatus: 10,
                });
              });
            });
          });
        };

        vm.selectedCount = function() {
          if (!vm.documents) return 0;
          const count = vm.documents.reduce(
            (cnt, document) => cnt + (document.selected ? 1 : 0),
            0,
          );

          return count;
        };

        vm.openSignatureDialogForSelectedDocuments = function() {
          const selectedDocuments = vm.documents.filter(x => x.selected);
          if (!selectedDocuments || selectedDocuments.length === 0) return;

          if (
            selectedDocuments.some(
              x => x.signingStatus == 10 || x.signingStatus == 20 || x.signingStatus == 21,
            )
          ) {
            flySvc.error('selectionContainsDocumentPendingOrAlreadySigned');
            return;
          }
          return vm.openSignatureDialog(selectedDocuments);
        };

        vm.canCheckout = function(document) {
          return (
            dataCacheService.hasUserConnectPlatform(46) && !!document && document.storageType !== 1
          );
        };

        vm.getCheckedoutTitle = function(document) {
          return appMessages('msgCheckedOutBy', {
            name: dataCacheService.getUserName(document.checkedOutByUserId),
          });
        };

        vm.canCheckin = function(document) {
          return (
            dataCacheService.hasUserConnectPlatform(46) &&
            document.checkedOutByUserId == AuthService.getUserId() &&
            document.storageType !== 1
          );
        };

        vm.canDiscardCheckout = function(document) {
          return (
            ((dataCacheService.hasUserConnectPlatform(46) &&
              document.checkedOutByUserId == AuthService.getUserId()) ||
              AuthService.hasPermission({ functionality: 'CompanyOwn', privilege: 'Update' })) &&
            document.storageType !== 1
          );
        };

        vm.checkinDocument = function(document) {
          if (document.checkedOutByUserId == AuthService.getUserId()) {
            checkin(document);
          }
        };

        vm.onDiscardCheckout = function(document) {
          dialogsSvc
            .showYesNo('comfirmTitle', 'msgDiscardCheckout', {
              name: dataCacheService.getUserName(document.checkedOutByUserId),
            })
            .then(function() {
              vm.discardCheckout(document);
            });
        };

        vm.openEditing = function(document) {
          DocumentService.getCheckedOutLink(document.id).then(link => {
            if (!link) return;
            window.open(link);
          });
        };

        vm.discardCheckout = function(document) {
          document.showSpinner = true;
          DocumentService.discardCheckout(document.id).then(
            () => {
              document.checkedOutByUserId = null;
              flySvc.success();
              document.showSpinner = false;
            },
            error => {
              console.error(error);
              if (error.response && error.response.data && error.response.data.code) {
                flySvc.error(error);
                document.showSpinner = false;
              } else {
                flySvc.success();
              }
              log.error('error discarding checkout ', error);
            },
          );
        };

        const checkin = function(document) {
          document.showSpinner = true;
          DocumentService.checkin(document.id).then(
            () => {
              document.checkedOutByUserId = null;
              flySvc.success();
              document.showSpinner = false;
            },
            error => {
              console.error(error);
              if (error.response && error.response.data && error.response.data.code) {
                flySvc.error(error);
                document.showSpinner = false;
              } else {
                flySvc.success();
              }
              log.error('error checking in document ', error);
            },
          );
        };

        vm.checkoutDocument = function(document, openinApp) {
          if (!document || !document.canEdit) return;
          document.showSpinner = true;
          DocumentService.checkout(document.id, openinApp).then(
            data => {
              if (!!data) {
                document.checkedOutByUserId = AuthService.getUserId();
                flySvc.success();
              }
              document.showSpinner = false;
            },
            error => {
              console.error(error);
              if (error.response && error.response.data && error.response.data.code) {
                flySvc.error(error);
                document.showSpinner = false;
              } else {
                flySvc.success();
              }
              log.error('error checking out document ', error);
            },
          );
        };

        vm.documentTagAccessUpdatedHandler = tagAccessInfo => {
          if (
            tagAccessInfo.noOneHasAccess ||
            (tagAccessInfo.usersWithAccess?.length > 0 &&
              tagAccessInfo.usersWithAccess.indexOf(AuthService.getUserId()) < 0)
          ) {
            var document = vm.documents.find(x => x.id === tagAccessInfo.documentId);
            if (document) {
              var index = vm.documents.indexOf(document);
              if (index >= 0) vm.documents.splice(index, 1);
            }
          }
        };

        vm.documentUpdatedHandler = (document, originalDocumentId) => {
          if (!document) return;
          $timeout(function() {
            // if the original document was replaced by the signed document, we have to replace this document in the array.
            const documentId = originalDocumentId || document.id;

            var doc = vm.documents.find(x => x.id == documentId);

            if (doc != null) {
              angular.extend(doc, mapDocument(document));
            }
          }, 0);
        };
        vm.documentDeletedHandler = documentId => {
          if (!documentId) return;
          $timeout(function() {
            var idx = vm.documents.findIndex(x => x.id == documentId);
            if (idx >= 0) vm.documents.splice(idx, 1);
          }, 0);
        };
      },
    ],
  };
});
